import React, { useState } from "react";
import { TopAdminNav } from "../../layouts/TopAdminNav";
import SchProfileSetting from "../../components/schProfileSetting";
import Onboarding from "../../components/Onboarding";
import ChanPasssord from "../../components/chanPasssord";
import Faq from "../../components/faq";
import ContactUs from "./ContactUs";
import BankAccountSetting from "../../components/BankAccountSetting";
import {
  ShieldCheck,
  HelpCircle,
  Mail,
  MessageSquare,
  School,
  Landmark,
  KeyRound,
  Eye,
  EyeOff,
  Loader2,
} from "lucide-react";
import toast from "react-hot-toast";
import { setTransactionPin } from "../../services/admin";

const TransactionPinSection = () => {
  const [form, setForm] = useState({ current_password: "", transaction_pin: "", transaction_pin_confirmation: "" });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const inputContainer = "relative flex items-center bg-slate-50 rounded-2xl border-2 border-transparent focus-within:border-blue-500 focus-within:bg-white transition-all px-5 py-4";
  const labelStyle = "text-[11px] font-black text-slate-400 uppercase tracking-widest mb-2 block ml-1";

  const handleSubmit = async () => {
    if (!form.current_password || !form.transaction_pin || !form.transaction_pin_confirmation) {
      toast.error("Please fill all fields."); return;
    }
    if (form.transaction_pin !== form.transaction_pin_confirmation) {
      toast.error("PINs do not match."); return;
    }
    if (!/^\d{6}$/.test(form.transaction_pin)) {
      toast.error("PIN must be exactly 6 digits."); return;
    }
    setLoading(true);
    const id = toast.loading("Saving transaction PIN...");
    try {
      await setTransactionPin(form);
      toast.success("Transaction PIN saved successfully.", { id });
      setForm({ current_password: "", transaction_pin: "", transaction_pin_confirmation: "" });
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to save PIN.", { id });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl animate-in fade-in duration-300">
      <div className="flex items-center gap-3 mb-8">
        <div className="p-3 bg-blue-50 text-blue-600 rounded-2xl">
          <KeyRound size={22} />
        </div>
        <div>
          <h3 className="text-xl font-black text-slate-800">Transaction PIN</h3>
          <p className="text-slate-400 font-medium text-xs mt-0.5">
            Set or update the 6-digit PIN used to authorise payment postings.
          </p>
        </div>
      </div>

      <div className="space-y-6">
        <div className="space-y-1">
          <label className={labelStyle}>Current Password</label>
          <div className={inputContainer}>
            <input
              type={showPassword ? "text" : "password"}
              value={form.current_password}
              onChange={(e) => setForm(f => ({ ...f, current_password: e.target.value }))}
              placeholder="Your account password"
              className="w-full bg-transparent outline-none text-slate-700 font-bold"
            />
            <button type="button" onClick={() => setShowPassword(v => !v)} className="text-slate-300 hover:text-slate-500 ml-3 shrink-0">
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-1">
            <label className={labelStyle}>New Transaction PIN</label>
            <div className={inputContainer}>
              <input
                type="password"
                value={form.transaction_pin}
                onChange={(e) => setForm(f => ({ ...f, transaction_pin: e.target.value.replace(/\D/g, "") }))}
                placeholder="6 digits"
                maxLength={6}
                className="w-full bg-transparent outline-none text-slate-700 font-bold tracking-widest"
              />
            </div>
          </div>
          <div className="space-y-1">
            <label className={labelStyle}>Confirm PIN</label>
            <div className={`${inputContainer} ${form.transaction_pin_confirmation && form.transaction_pin !== form.transaction_pin_confirmation ? "border-rose-400" : ""}`}>
              <input
                type="password"
                value={form.transaction_pin_confirmation}
                onChange={(e) => setForm(f => ({ ...f, transaction_pin_confirmation: e.target.value.replace(/\D/g, "") }))}
                placeholder="Re-enter 6-digit PIN"
                maxLength={6}
                className="w-full bg-transparent outline-none text-slate-700 font-bold tracking-widest"
              />
            </div>
            {form.transaction_pin_confirmation && form.transaction_pin !== form.transaction_pin_confirmation && (
              <p className="text-[11px] text-rose-500 font-bold ml-1">PINs do not match</p>
            )}
          </div>
        </div>

        <button
          type="button"
          onClick={handleSubmit}
          disabled={loading || !form.current_password || !form.transaction_pin || form.transaction_pin !== form.transaction_pin_confirmation}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-2xl font-black text-xs uppercase tracking-widest transition-all shadow-lg shadow-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? <><Loader2 size={15} className="animate-spin" /> Saving...</> : <><KeyRound size={15} /> Save PIN</>}
        </button>
      </div>
    </div>
  );
};

const SchProfile = () => {
  const [active, setActive] = useState("3");
  const userString = sessionStorage.getItem("user");
  const role = userString ? JSON.parse(userString)?.role : "";

  const menuItems = [
    { id: "1", label: "School Profile", icon: <School size={18} />, show: role === "super_admin" },
    { id: "6", label: "Bank Accounts", icon: <Landmark size={18} />, show: true },
    { id: "3", label: "Security", icon: <ShieldCheck size={18} />, show: true },
    { id: "7", label: "Transaction PIN", icon: <KeyRound size={18} />, show: true },
    { id: "4", label: "Help & FAQ", icon: <HelpCircle size={18} />, show: true },
    { id: "5", label: "Contact Us", icon: <Mail size={18} />, show: true },
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <TopAdminNav title="Settings" name="Jane Doe" />

      <div className="max-w-[1400px] mx-auto p-4 md:p-8 flex flex-col lg:flex-row gap-8">
        {/* Sidebar Navigation */}
        <aside className="w-full lg:w-72 space-y-6">
          <div className="bg-white rounded-[2rem] p-4 shadow-sm border border-slate-100">
            <nav className="space-y-2">
              {menuItems.map(
                (item) =>
                  item.show && (
                    <button
                      key={item.id}
                      onClick={() => setActive(item.id)}
                      className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl font-bold text-sm transition-all ${
                        active === item.id
                          ? "bg-blue-600 text-white shadow-lg shadow-blue-100"
                          : "text-slate-500 hover:bg-slate-50"
                      }`}
                    >
                      {item.icon}
                      {item.label}
                    </button>
                  ),
              )}
            </nav>

            <div className="mt-8 pt-6 border-t border-slate-100">
              <a
                href="https://wa.me/+23409137826058"
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-center gap-2 w-full py-4 bg-green-500 hover:bg-green-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest transition-all shadow-lg shadow-green-100"
              >
                <MessageSquare size={16} />
                Chat Support
              </a>
            </div>
          </div>
        </aside>

        {/* Content Area */}
        <main className="flex-1 min-w-0">
          <div className="bg-white rounded-[2.5rem] p-6 md:p-10 shadow-sm border border-slate-100 min-h-[70vh] animate-in fade-in slide-in-from-bottom-4 duration-500 overflow-hidden">
            {active === "1" && <SchProfileSetting />}
            {active === "2" && <Onboarding />}
            {active === "3" && <ChanPasssord />}
            {active === "7" && <TransactionPinSection />}
            {active === "4" && <Faq />}
            {active === "5" && <ContactUs />}
            {active === "6" && <BankAccountSetting />}
          </div>
        </main>
      </div>
    </div>
  );
};

export default SchProfile;
